import { useCallback } from "react";
import { NavTreeNode } from "@/utils/types";
import {
  findElementByDomUniquePath,
  getCodebaseIdFromNode,
} from "@/components/Canvas/utils/utils";
import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import _ from "lodash";

export const getFirstPositionedParent = (
  iframeWindow?: Window,
  parentElement?: HTMLElement,
) => {
  if (!iframeWindow || !parentElement) {
    return null;
  }

  let currentParent: HTMLElement | null = parentElement;
  while (currentParent) {
    const computedStyle = iframeWindow.getComputedStyle(currentParent);
    const position = computedStyle.position;
    if (
      position === "absolute" ||
      position === "fixed" ||
      position === "relative" ||
      position === "sticky"
    ) {
      return currentParent;
    }
    currentParent = currentParent.parentElement;
  }
  return null;
};

export const calculateDropPosition = (
  mouseX: number,
  mouseY: number,
  parentNavNode: NavTreeNode,
  iframeDocument: Document,
  forAbsolute?: boolean,
): {
  position?: {
    beforeCodebaseId?: string;
    afterCodebaseId?: string;
    index?: number;
  } | null;
  dropCoordinatesInSb?: { x: number; y: number };
  dropCoordinatesForAbsolute?: { x: number; y: number };
} => {
  if (!iframeDocument) {
    return {};
  }

  const dropCoordinatesInSb = {
    x: mouseX,
    y: mouseY,
  };

  const dropCoordinatesForAbsolute = {
    x: mouseX,
    y: mouseY,
  };

  if (!parentNavNode || !parentNavNode.children?.length) {
    return { dropCoordinatesInSb, dropCoordinatesForAbsolute };
  }

  const parentElement = findElementByDomUniquePath(
    iframeDocument,
    parentNavNode.domUniquePath ?? parentNavNode.domUniquePathForComponent,
  );

  if (!parentElement) {
    return { dropCoordinatesInSb, dropCoordinatesForAbsolute };
  }

  const iframeWindow = iframeDocument.defaultView;
  if (!iframeWindow) {
    return { dropCoordinatesInSb, dropCoordinatesForAbsolute };
  }

  // Go up the tree, find the first parent that is positioned to get the relative drop coordinates
  const positionedParent = getFirstPositionedParent(
    iframeWindow,
    parentElement,
  );
  if (positionedParent) {
    const rect = positionedParent.getBoundingClientRect();
    dropCoordinatesForAbsolute.x -= rect.left;
    dropCoordinatesForAbsolute.y -= rect.top;
  }

  // No need to calculate the position as it's absolutely moving
  if (forAbsolute) {
    return { dropCoordinatesInSb, dropCoordinatesForAbsolute };
  }

  // Get all direct children that are not absolutely or fixed positioned
  const childElements: {
    element: HTMLElement;
    codebaseId: string;
    rect: DOMRect;
    originalIndex: number;
  }[] = [];

  for (let i = 0; i < parentNavNode.children.length; i++) {
    const childNode = parentNavNode.children[i];
    try {
      const childElement = findElementByDomUniquePath(
        iframeDocument,
        childNode.domUniquePath ?? childNode.domUniquePathForComponent,
      );

      if (childElement && childElement.parentElement === parentElement) {
        const targetWindow = iframeWindow;
        const computedStyle = targetWindow.getComputedStyle(childElement);
        const position = computedStyle.position;

        // Skip absolutely and fixed positioned elements
        if (position !== "absolute" && position !== "fixed") {
          const codebaseId = getCodebaseIdFromNode(childElement);
          if (codebaseId) {
            childElements.push({
              element: childElement,
              codebaseId,
              rect: childElement.getBoundingClientRect(),
              originalIndex: i,
            });
          }
        }
      }
    } catch (error) {
      console.error("Error calculating drop position", error);
    }
  }

  if (childElements.length === 0) {
    return { dropCoordinatesInSb, dropCoordinatesForAbsolute };
  }

  // Determine the parent's layout type
  const targetWindow = iframeWindow;
  const parentStyle = targetWindow.getComputedStyle(parentElement);
  const display = parentStyle.display;
  const flexDirection = parentStyle.flexDirection;

  // Determine if we should compare horizontally or vertically
  let compareHorizontally = false;
  let compareVertically = true;

  if (display === "flex") {
    if (flexDirection === "row" || flexDirection === "row-reverse") {
      compareHorizontally = true;
      compareVertically = false;
    }
  } else if (
    display === "inline" ||
    display === "inline-block" ||
    display === "inline-flex"
  ) {
    // For inline elements, we might need both comparisons
    compareHorizontally = true;
    compareVertically = true;
  } else if (display === "grid") {
    // For grid, we need more complex logic
    // For now, we'll use a simple approach based on position
    compareHorizontally = true;
    compareVertically = true;
  }

  // Find the insertion position
  let insertBeforeIndex = -1;
  let minDistance = Infinity;

  for (let i = 0; i < childElements.length; i++) {
    const child = childElements[i];
    const rect = child.rect;

    let distance = 0;

    if (compareHorizontally && compareVertically) {
      // For grid or inline layouts, use 2D distance
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      // Check if mouse is before this element in reading order
      if (
        mouseY < centerY ||
        (Math.abs(mouseY - centerY) < rect.height / 2 && mouseX < centerX)
      ) {
        distance = Math.sqrt(
          Math.pow(mouseX - centerX, 2) + Math.pow(mouseY - centerY, 2),
        );

        if (distance < minDistance) {
          minDistance = distance;
          insertBeforeIndex = i;
        }
      }
    } else if (compareHorizontally) {
      // For horizontal layouts (flex row)
      const centerX = rect.left + rect.width / 2;

      if (mouseX < centerX) {
        distance = Math.abs(mouseX - centerX);

        if (distance < minDistance) {
          minDistance = distance;
          insertBeforeIndex = i;
        }
      }
    } else if (compareVertically) {
      // For vertical layouts (block, flex column)
      const centerY = rect.top + rect.height / 2;

      if (mouseY < centerY) {
        distance = Math.abs(mouseY - centerY);

        if (distance < minDistance) {
          minDistance = distance;
          insertBeforeIndex = i;
        }
      }
    }
  }

  // Determine the drop position
  if (insertBeforeIndex === -1) {
    // Insert after the last element
    const lastChild = childElements[childElements.length - 1];
    return {
      position: {
        afterCodebaseId: lastChild.codebaseId,
        index: parentNavNode.children.length,
      },
      dropCoordinatesInSb,
      dropCoordinatesForAbsolute,
    };
  } else {
    // Insert before the found element
    return {
      position: {
        beforeCodebaseId: childElements[insertBeforeIndex].codebaseId,
        index: childElements[insertBeforeIndex].originalIndex,
      },
      dropCoordinatesInSb,
      dropCoordinatesForAbsolute,
    };
  }
};

export const calculateAndSetDropPosition = _.throttle(
  (
    mouseX: number,
    mouseY: number,
    parentNavNode: NavTreeNode,
    iframeDocument: Document,
    forAbsolute?: boolean,
  ) => {
    const { position, dropCoordinatesInSb, dropCoordinatesForAbsolute } =
      calculateDropPosition(
        mouseX,
        mouseY,
        parentNavNode,
        iframeDocument,
        forAbsolute,
      );

    useCanvasStore.getState().setDropPosition({
      position,
      dropCoordinatesInSb,
      dropCoordinatesForAbsolute,
    });
  },
  100,
);
